import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import Button from '../ui/Button';
import IconButton from '../ui/IconButton';
import useLayer from './useLayer';
import './overlays.css';

/**
 * Centred modal — 480px, or 640px when `size="form"`.
 * Destructive confirmations use a filled crimson primary and must name the record.
 */
export default function Modal({
  open,
  onClose,
  title,
  children,
  size = 'default',
  confirmLabel,
  onConfirm,
  confirmLoading = false,
  confirmDisabled = false,
  destructive = false,
  cancelLabel = 'Cancel',
  hideCancel = false,
  footer,
}) {
  const panelRef = useRef(null);
  useLayer({ open, onClose, ref: panelRef });

  if (!open) return null;

  return createPortal(
    <div className="sv-overlay sv-overlay--modal" onMouseDown={onClose}>
      <div
        ref={panelRef}
        className={`sv-modal sv-modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="sv-modal__head">
          <h2 className="sv-modal__title">{title}</h2>
          <IconButton icon="bx-x" size="sm" label="Close" onClick={onClose} />
        </div>

        <div className="sv-modal__body">{children}</div>

        <div className="sv-modal__foot">
          {footer || (
            <>
              {!hideCancel && (
                <Button variant="secondary" onClick={onClose} disabled={confirmLoading}>
                  {cancelLabel}
                </Button>
              )}
              {confirmLabel && (
                <Button
                  variant={destructive ? 'destructive' : 'primary'}
                  onClick={onConfirm}
                  loading={confirmLoading}
                  disabled={confirmDisabled}
                >
                  {confirmLabel}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
